import React from "react";
import "./ConsultationCard.scss";

import StarRating from "../../../atoms/StarRating/StarRating";
import { ConsultationRow } from "../../../types/consultation";
import {
  CONSULTATION_SEGMENTS,
  consultationStatusClass,
} from "./consultations.constants";

import { ReactComponent as ClockIcon } from "../../../assets/icons/patient-layout-v2/consults/ClockIcon.svg";
import { ReactComponent as VideoIcon } from "../../../assets/icons/patient-layout-v2/consults/VideoIcon.svg";

const DEFAULT_ACCENT = "var(--ink-2)";

// Only for a tag the stylesheet has no rule for; known ones are painted from
// the design tokens so the pills match the rest of the module.
const fallbackTagStyle = (tag?: {
  textColor?: string;
  backgroundColor?: string;
}) =>
  ({
    color: tag?.textColor,
    background: tag?.backgroundColor,
    borderColor: tag?.textColor ? `${tag.textColor}33` : undefined,
  }) as React.CSSProperties;

interface ConsultationCardProps {
  row: ConsultationRow;
  /** Upcoming cards carry the accent rail and the join action. */
  isUpcoming?: boolean;
}

const ConsultationCard: React.FC<ConsultationCardProps> = ({
  row,
  isUpcoming,
}) => {
  const rating = row.rating;
  const ctaLabel = row.cta?.label;
  const ctaUrl = row.cta?.url || row.cta?.link || null;

  const segment = CONSULTATION_SEGMENTS[row.category];
  const statusClass = consultationStatusClass(row.statusTag?.label);

  return (
    <div
      className={`cs-item ${isUpcoming ? "is-upcoming" : ""}`}
      style={
        {
          ["--c" as any]: segment?.color || row.accentColor || DEFAULT_ACCENT,
          ["--c-50" as any]: segment?.tint || "var(--surface-2)",
        } as React.CSSProperties
      }
    >
      <div className="cs-when">
        <div className="cs-day">{row.dateBlock?.day || "--"}</div>
        <div className="cs-mon">{row.dateBlock?.month || ""}</div>
        <div className="cs-yr">{row.dateBlock?.year || ""}</div>
      </div>

      <div className="cs-main">
        <div className="cs-top">
          <span className="cs-coach">{row.coachName || "Coach"}</span>
          {row.roleTag?.label && (
            <span
              className="cs-seg"
              style={segment ? undefined : fallbackTagStyle(row.roleTag)}
            >
              {row.roleTag.label}
            </span>
          )}
          {row.statusTag?.label && (
            <span
              className={`cs-st ${statusClass}`}
              style={statusClass ? undefined : fallbackTagStyle(row.statusTag)}
            >
              {row.statusTag.label}
            </span>
          )}
        </div>

        <div className="cs-meta">
          {row.timeLabel && (
            <span>
              <ClockIcon />
              {row.timeLabel}
            </span>
          )}
          {row.modeLabel && (
            <span>
              <VideoIcon />
              {row.modeLabel}
            </span>
          )}
        </div>

        {row.notes && (
          <div className="cs-notes">
            <b>Notes:</b> {row.notes}
          </div>
        )}

        {rating && (
          <div className="cs-rating">
            <span className="cs-rating-lead">Patient rating</span>
            <StarRating value={rating.value} />
            <span className="cs-rating-num">{rating.value.toFixed(1)}</span>
            {rating.review ? (
              <span className="cs-review">{rating.review}</span>
            ) : (
              <span className="cs-norev">No written review</span>
            )}
          </div>
        )}
      </div>

      {!!ctaLabel &&
        (ctaUrl ? (
          <a
            className="cs-join"
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <VideoIcon />
            {ctaLabel}
          </a>
        ) : (
          <span className="cs-join cs-join-inactive">
            <VideoIcon />
            {ctaLabel}
          </span>
        ))}
    </div>
  );
};

export default ConsultationCard;
